import { useMemo, type ReactElement, type ReactNode } from 'react'
import {
  isDefined,
  isEmpty,
  type DashboardWidget,
  type ReportColumnDefinition,
  type TabularResult,
} from '@terros/common'
import { SimpleGrid, Stack, Text, RingProgress } from '@mantine/core'
import {
  getMetricGridRows,
  getQuartileColor,
  getSelectedReportColumnDisplayLabel,
  SizePrimitives,
  type ReportColumnVariantGroup,
  useReportColumnsMetadata,
} from '@front/model'

type Props = {
  widget: Extract<DashboardWidget, { type: 'number' }>
  data: TabularResult
}

export function NumberWidget({ data, widget }: Props): ReactElement {
  const { columnsMap, variantMap } = useReportColumnsMetadata()
  const values = useMemo(
    () =>
      widget.metrics.map((metric, index) =>
        toDisplayValue({ metric, index, data, widget, columnsMap, variantMap })
      ),
    [data, columnsMap, variantMap, widget]
  )
  const rows = useMemo(() => getMetricGridRows(values), [values])

  return (
    <Stack gap='sm'>
      {rows.map((row) => (
        <SimpleGrid cols={row.length} key={row.map(({ label }) => label).join('|')} spacing='sm'>
          {row.map(({ gauge, label, threshold, value }) => (
            <Stack align='center' gap={4} key={label} style={{ minWidth: 0 }}>
              <MetricValueFrame metricCount={values.length}>
                {gauge && typeof threshold === 'number' ? (
                  <GaugeValue metricCount={values.length} threshold={threshold} value={value} />
                ) : (
                  <NumberValue metricCount={values.length} threshold={threshold} value={value} />
                )}
              </MetricValueFrame>
              <Text c='dimmed' lineClamp={2} size='xs' ta='center' style={{ maxWidth: '100%' }}>
                {label}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
      ))}
    </Stack>
  )
}

function toDisplayValue({
  metric,
  index,
  data,
  widget,
  columnsMap,
  variantMap,
}: {
  metric: Extract<DashboardWidget, { type: 'number' }>['metrics'][number]
  index: number
  data: TabularResult
  widget: Extract<DashboardWidget, { type: 'number' }>
  columnsMap: Map<string, ReportColumnDefinition>
  variantMap: Map<string, ReportColumnVariantGroup>
}): {
  label: string
  gauge: boolean
  threshold: number | null | undefined
  value: number | null
} {
  const selectedColumns = new Set(widget.metrics.map((metric) => metric.kpi))
  return {
    label: getSelectedReportColumnDisplayLabel({
      value: metric.kpi,
      selectedColumns,
      columnsMap,
      variantMap,
    }),
    gauge: metric.gauge === true,
    threshold: metric.threshold,
    value: getMetricValue(data, index),
  }
}

function MetricValueFrame({ children, metricCount }: { children: ReactNode; metricCount: number }): ReactElement {
  return (
    <div
      className='flex w-full min-w-0 items-center justify-center'
      style={{ height: SizePrimitives[metricCount === 1 ? '32' : '24'] }}
    >
      {children}
    </div>
  )
}

function NumberValue({
  metricCount,
  threshold,
  value,
}: {
  metricCount: number
  threshold: number | null | undefined
  value: number | null
}): ReactElement {
  // Colored by progress against the target, matching the gauge's ring and the
  // mobile app. Without a target there is nothing to measure against, so the
  // value keeps the default text color.
  const percentage = getPercentage(value, threshold ?? undefined)

  return (
    <Text
      c={isDefined(percentage) ? getQuartileColor(percentage) : undefined}
      fw={700}
      ta='center'
      style={{
        fontSize: getValueFontSize(metricCount),
        lineHeight: 1.1,
        maxWidth: '100%',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}
    >
      <FormattedValue value={value} threshold={threshold} />
    </Text>
  )
}

function GaugeValue({
  metricCount,
  threshold,
  value,
}: {
  metricCount: number
  threshold: number
  value: number | null
}): ReactElement {
  const percentage = getPercentage(value, threshold)

  return (
    <RingProgress
      label={
        <Text fw={700} size={metricCount === 1 ? 'xl' : 'sm'} ta='center'>
          {formatGaugeValue(value)}
        </Text>
      }
      sections={[{ value: percentage ?? 0, color: getQuartileColor(percentage ?? 0) }]}
      size={SizePrimitives[metricCount === 1 ? '32' : '24']}
      thickness={SizePrimitives['4']}
    />
  )
}

function getMetricValue(data: TabularResult, index: number): number | null {
  if (isEmpty(data.rows)) return null
  const value = data.rows[0][index]
  if (typeof value !== 'number') return null
  return value
}

function FormattedValue({
  value,
  threshold,
}: {
  value: number | null
  threshold: number | null | undefined
}): ReactNode {
  if (value === null) return '-'
  const formattedValue = value.toLocaleString()
  if (typeof threshold !== 'number') return formattedValue
  return (
    <>
      {formattedValue}
      <wbr />/<wbr />
      {threshold.toLocaleString()}
    </>
  )
}

function formatGaugeValue(value: number | null): string {
  if (value === null) return '-'
  return value.toLocaleString()
}

function getPercentage(value: number | null, threshold: number | undefined): number | undefined {
  if (value === null || threshold === undefined || threshold <= 0) return undefined
  return Math.min(100, Math.max(0, (value / threshold) * 100))
}

function getValueFontSize(metricCount: number): number {
  if (metricCount >= 3) return SizePrimitives['4']
  if (metricCount === 2) return SizePrimitives['6']
  return SizePrimitives['12']
}
